/**
 * Authentication Utility
 * Session is stored in sessionStorage (cleared when the browser tab closes).
 * Logout clears all auth data from both session and local storage.
 */

import { getHomePathForRole, normalizeRole } from '../utils/roles';

const AUTH_USER_KEY = 'user';
const AUTH_TOKEN_KEY = 'authToken';

const clearAuthFrom = (store) => {
  if (!store) return;
  store.removeItem(AUTH_USER_KEY);
  store.removeItem(AUTH_TOKEN_KEY);
};

// Drop legacy persistent login from older builds
clearAuthFrom(localStorage);

const auth = {
  /**
   * Save authentication data for the current browser tab/session
   * @param {string} token - Session marker
   * @param {Object} user - User object
   */
  saveAuth: (token, user) => {
    sessionStorage.setItem(AUTH_TOKEN_KEY, token || 'logged-in');
    sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    clearAuthFrom(localStorage);
  },

  /**
   * Clear all authentication data
   */
  clearAuth: () => {
    clearAuthFrom(sessionStorage);
    clearAuthFrom(localStorage);
  },

  /**
   * Get stored token
   * @returns {string|null}
   */
  getToken: () => {
    return sessionStorage.getItem(AUTH_TOKEN_KEY);
  },

  /**
   * Get stored user
   * @returns {Object|null}
   */
  getUser: () => {
    const userStr = sessionStorage.getItem(AUTH_USER_KEY);
    try {
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      auth.clearAuth();
      return null;
    }
  },

  /**
   * Check if user is authenticated in this browser session
   * @returns {boolean}
   */
  isAuthenticated: () => {
    return !!sessionStorage.getItem(AUTH_USER_KEY);
  },

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check (e.g., 'admin', 'user')
   * @returns {boolean}
   */
  hasRole: (role) => {
    const user = auth.getUser();
    return user && normalizeRole(user.role) === normalizeRole(role);
  },

  /**
   * Check if user has any of the specified roles
   * @param {Array<string>} roles - Array of roles to check
   * @returns {boolean}
   */
  hasAnyRole: (roles) => {
    const user = auth.getUser();
    if (!user) return false;
    const userRole = normalizeRole(user.role);
    return roles.some((role) => normalizeRole(role) === userRole);
  },

  /**
   * Home route for the current user's role
   * @returns {string}
   */
  getHomePath: () => {
    const user = auth.getUser();
    return getHomePathForRole(user?.role);
  },

  /**
   * Decode JWT token (basic decoding, doesn't verify signature)
   * @param {string} token - JWT token
   * @returns {Object|null}
   */
  decodeToken: (token) => {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  },

  /**
   * Check if token is expired
   * @param {string} token - JWT token (optional, uses stored token if not provided)
   * @returns {boolean}
   */
  isTokenExpired: () => false,

  /**
   * Get user ID from stored user
   * @returns {string|null}
   */
  getUserId: () => {
    const user = auth.getUser();
    return user ? user._id || user.id : null;
  },

  /**
   * Get username from stored user
   * @returns {string|null}
   */
  getUsername: () => {
    const user = auth.getUser();
    return user ? user.username : null;
  },

  /**
   * Check if user is approved
   * @returns {boolean}
   */
  isApproved: () => {
    const user = auth.getUser();
    return user?.status === 'approved';
  },

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check (e.g., 'students:view')
   * @returns {boolean}
   */
  hasPermission: (permission) => {
    const user = auth.getUser();
    if (!user) return false;
    const permissions = user.permissions || [];
    return permissions.includes(permission);
  },

  /**
   * Check if user has any of the specified permissions
   * @param {Array<string>} permissions - Array of permissions to check
   * @returns {boolean}
   */
  hasAnyPermission: (permissions) => {
    const user = auth.getUser();
    if (!user) return false;
    const userPermissions = user.permissions || [];
    return permissions.some((perm) => userPermissions.includes(perm));
  },

  /**
   * Get user's approval status
   * @returns {string} - 'pending', 'approved', or 'rejected'
   */
  getApprovalStatus: () => {
    const user = auth.getUser();
    return user?.status || 'pending';
  },

  /**
   * Get user's approved permissions
   * @returns {Array<string>}
   */
  getPermissions: () => {
    const user = auth.getUser();
    return user?.permissions || [];
  },
};

export default auth;
